import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, Bookmark, Info } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { calcBudget, calcEmi, calcEmiPlan, calcRentVsBuy, calcInvestment, calcSavingsGoalPlan, formatCurrency } from "@/lib/calculators";
import { REGISTRY, CUSTOM, GROUPS, calcById } from "@/lib/calcRegistry";
import { Donut, GrowthBars, Legend } from "@/components/CalcChart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const DEFAULT_TOOL = "budget";

export default function Calculators() {
  const { tool } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const known = CUSTOM[tool] || REGISTRY[tool];
  const active = known ? tool : DEFAULT_TOOL;
  const L = (obj) => (language === "hi" ? obj.hi : obj.en);

  const allTools = [
    ...Object.entries(CUSTOM).map(([id, c]) => ({ id, ...c })),
    ...Object.entries(REGISTRY).map(([id, c]) => ({ id, ...c })),
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">{t("calculators_title")}</h1>
        <p className="mt-1.5 text-slate-500">{t("calculators_subtitle")}</p>

        <div className="mt-6 space-y-4" data-testid="calc-groups">
          {GROUPS.map((g) => {
            const tools = allTools.filter((x) => x.group === g.id);
            if (tools.length === 0) return null;
            return (
              <div key={g.id}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{L(g.name)}</p>
                <div className="flex flex-wrap gap-2">
                  {tools.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button key={tab.id} data-testid={`calc-tab-${tab.id}`} onClick={() => navigate(`/calculators/${tab.id}`)}
                        className={`inline-flex min-h-[42px] items-center gap-2 whitespace-nowrap rounded-full border px-3.5 text-sm font-medium transition-colors ${active === tab.id ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-600 hover:border-primary/40"}`}>
                        <Icon className="h-4 w-4" /> {L(tab.name)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8" data-testid={`calc-panel-${active}`}>
          {active === "budget" && <BudgetCalc />}
          {active === "investment" && <InvestmentCalc />}
          {active === "savings-goal" && <SavingsGoalCalc />}
          {active === "emi" && <EmiCalc />}
          {active === "rent-vs-buy" && <RentBuyCalc />}
          {active === "mileage" && <MileageCalc />}
          {REGISTRY[active] && active !== "mileage" && <GenericCalc id={active} />}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function useSaveCalc() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const isAuthed = user && typeof user === "object";
  const save = async ({ title, content, metadata }) => {
    if (!isAuthed) { navigate("/login"); return; }
    try {
      await api.post("/saved-items", { type: "calculation", title, content, tags: ["calculator"], metadata });
      toast.success(t("calculation_saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("error_generic"));
    }
  };
  return { save, isAuthed };
}

function Field({ label, value, onChange, testId, suffix, type = "number" }) {
  return (
    <div>
      <Label className="text-sm text-slate-600">{label}</Label>
      <div className="relative mt-1.5">
        <Input type={type} inputMode={type === "number" ? "decimal" : undefined} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="min-h-[48px]" />
        {suffix && type === "number" && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{suffix}</span>}
      </div>
    </div>
  );
}

function ResultRow({ label, value, big, accent }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className={`text-sm ${big ? "font-semibold text-slate-900" : "text-slate-500"}`}>{label}</span>
      <span className={`font-mono font-semibold ${big ? "text-xl text-primary" : accent ? "text-slate-900" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}

const Card = ({ children }) => <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-premium">{children}</div>;

function SaveButton({ onSave, isAuthed, testId }) {
  const { t } = useLanguage();
  return (
    <Button onClick={onSave} className="mt-5 w-full gap-1.5 rounded-full min-h-[44px]" data-testid={testId}>
      <Bookmark className="h-4 w-4" /> {isAuthed ? t("save_calculation") : t("result_login_to_save")}
    </Button>
  );
}

// ---------- Generic (registry-driven) ----------
function GenericCalc({ id }) {
  const { t, language } = useLanguage();
  const { save, isAuthed } = useSaveCalc();
  const def = calcById(id);
  const L = (obj) => (obj ? (language === "hi" ? obj.hi : obj.en) : "");
  const [values, setValues] = useState(() => {
    const init = {};
    def.fields.forEach((f) => { init[f.key] = f.default; });
    return init;
  });
  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const reset = () => {
    const init = {};
    def.fields.forEach((f) => { init[f.key] = f.default; });
    setValues(init);
  };
  const fmt = (n) => formatCurrency(n, language);
  const out = def.compute(values, fmt);

  const onSave = () => save({
    title: `${L(def.name)}`,
    content: out.rows.map((r) => `${L(r.label)}: ${r.value}`).join("\n") + (out.verdict ? `\n${L(out.verdict)}` : ""),
    metadata: { calculator: id, inputs: values, results: out.rows.map((r) => ({ label: L(r.label), value: r.value })) },
  });

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <div className="space-y-4">
          {def.fields.map((f) => {
            if (f.type === "toggle") {
              return (
                <div key={f.key} className="flex items-center justify-between gap-3">
                  <Label className="text-sm text-slate-600">{L(f.label)}</Label>
                  <Switch checked={!!values[f.key]} onCheckedChange={(v) => set(f.key, v)} data-testid={`calc-field-${f.key}`} />
                </div>
              );
            }
            if (f.type === "select") {
              return (
                <div key={f.key}>
                  <Label className="text-sm text-slate-600">{L(f.label)}</Label>
                  <select
                    value={values[f.key]}
                    onChange={(e) => set(f.key, isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value))}
                    data-testid={`calc-field-${f.key}`}
                    className="mt-1.5 min-h-[48px] w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {f.options.map((o) => (
                      <option key={o.value} value={o.value}>{L(o.label)}</option>
                    ))}
                  </select>
                </div>
              );
            }
            return (
              <Field key={f.key} label={L(f.label)} value={values[f.key]} onChange={(v) => set(f.key, v)}
                testId={`calc-field-${f.key}`} suffix={f.suffix} type={f.type === "date" ? "date" : f.type === "time" ? "time" : "number"} />
            );
          })}
        </div>
        <Button variant="ghost" size="sm" onClick={reset} data-testid="calc-reset-btn" className="mt-4 text-slate-500">
          {t("reset")}
        </Button>
      </Card>
      <Card>
        <div className="divide-y divide-slate-100" data-testid="calc-results">
          {out.rows.map((r, i) => (
            <ResultRow key={i} label={L(r.label)} value={r.value} big={r.big} accent={r.accent} />
          ))}
        </div>
        {out.verdict && (
          <div className="mt-4 rounded-xl bg-primary/10 p-4 text-center" data-testid="calc-verdict">
            <p className="font-display text-base font-bold text-primary">{L(out.verdict)}</p>
          </div>
        )}
        <SaveButton onSave={onSave} isAuthed={isAuthed} testId="calc-save-btn" />
      </Card>
    </div>
  );
}

// ---------- Advanced Mileage / Fuel Efficiency ----------
const FUEL_TYPES = [
  { id: "petrol", label: { en: "Petrol", hi: "पेट्रोल" }, unit: "L", price: 100 },
  { id: "diesel", label: { en: "Diesel", hi: "डीज़ल" }, unit: "L", price: 90 },
  { id: "cng", label: { en: "CNG", hi: "सीएनजी" }, unit: "kg", price: 75 },
  { id: "ev", label: { en: "Electric", hi: "इलेक्ट्रिक" }, unit: "kWh", price: 9 },
];
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const round2 = (n) => Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;

function MileageCalc() {
  const { t, language } = useLanguage();
  const { save, isAuthed } = useSaveCalc();
  const L = (o) => (o ? (language === "hi" ? o.hi : o.en) : "");
  const fmt = (n) => formatCurrency(n, language);

  const [fuel, setFuel] = useState("petrol");
  const ft = FUEL_TYPES.find((f) => f.id === fuel);
  const unit = ft.unit;
  const effUnit = `km/${unit}`;

  const [distance, setDistance] = useState(400);
  const [used, setUsed] = useState(fuel === "ev" ? 40 : 22);
  const [price, setPrice] = useState(ft.price);
  const [monthlyKm, setMonthlyKm] = useState(1000);
  const [advanced, setAdvanced] = useState(false);
  const [tripKm, setTripKm] = useState(300);
  const [fuelAmount, setFuelAmount] = useState(10);
  const [compare, setCompare] = useState(false);
  const [bEff, setBEff] = useState(20);
  const [bPrice, setBPrice] = useState(ft.price);

  const pickFuel = (id) => {
    setFuel(id);
    const f = FUEL_TYPES.find((x) => x.id === id);
    setPrice(f.price); setBPrice(f.price);
    setUsed(id === "ev" ? 40 : 22);
  };

  const d = num(distance), u = num(used), p = num(price);
  const eff = u > 0 ? d / u : 0;                       // km per unit
  const consumption = d > 0 ? (u / d) * 100 : 0;       // unit per 100 km
  const costPerKm = eff > 0 ? p / eff : 0;
  const tripFuelCost = u * p;                          // cost for entered distance
  const mUse = eff > 0 ? num(monthlyKm) / eff : 0;
  const mCost = mUse * p;

  const invalid = u <= 0 || d <= 0;

  const rows = [
    { label: { en: `Efficiency`, hi: `दक्षता` }, value: `${round2(eff)} ${effUnit}`, big: true },
    { label: { en: `Consumption`, hi: `खपत` }, value: `${round2(consumption)} ${unit}/100km` },
    { label: { en: "Cost per km", hi: "प्रति किमी लागत" }, value: fmt(round2(costPerKm)), accent: true },
    { label: { en: `Fuel cost (${round2(d)} km)`, hi: `ईंधन लागत (${round2(d)} किमी)` }, value: fmt(Math.round(tripFuelCost)) },
    { label: { en: "Monthly fuel cost", hi: "मासिक ईंधन लागत" }, value: fmt(Math.round(mCost)) },
    { label: { en: "Yearly fuel cost", hi: "वार्षिक ईंधन लागत" }, value: fmt(Math.round(mCost * 12)) },
    { label: { en: `Monthly fuel use`, hi: `मासिक ईंधन` }, value: `${round2(mUse)} ${unit}` },
    { label: { en: `Yearly fuel use`, hi: `वार्षिक ईंधन` }, value: `${round2(mUse * 12)} ${unit}` },
  ];

  const advRows = advanced ? [
    { label: { en: `Distance with ${round2(num(fuelAmount))} ${unit}`, hi: `${round2(num(fuelAmount))} ${unit} से दूरी` }, value: `${round2(num(fuelAmount) * eff)} km` },
    { label: { en: `Fuel for ${round2(num(tripKm))} km trip`, hi: `${round2(num(tripKm))} किमी के लिए ईंधन` }, value: eff > 0 ? `${round2(num(tripKm) / eff)} ${unit}` : "—" },
    { label: { en: `Cost for ${round2(num(tripKm))} km trip`, hi: `${round2(num(tripKm))} किमी की लागत` }, value: eff > 0 ? fmt(Math.round((num(tripKm) / eff) * p)) : "—" },
  ] : [];

  // A vs B comparison
  const aCostKm = costPerKm;
  const bCostKm = num(bEff) > 0 ? num(bPrice) / num(bEff) : 0;
  const aMonthly = mCost, bMonthly = num(bEff) > 0 ? (num(monthlyKm) / num(bEff)) * num(bPrice) : 0;
  const cheaper = aCostKm === bCostKm ? "tie" : aCostKm < bCostKm ? "A" : "B";
  const monthlySaving = Math.abs(aMonthly - bMonthly);

  const onSave = () => save({
    title: L({ en: "Mileage", hi: "माइलेज" }),
    content: rows.concat(advRows).map((r) => `${L(r.label)}: ${r.value}`).join("\n"),
    metadata: { calculator: "mileage", inputs: { fuel, distance, used, price, monthlyKm }, results: rows.map((r) => ({ label: L(r.label), value: r.value })) },
  });

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <Label className="text-sm text-slate-600">{L({ en: "Fuel type", hi: "ईंधन प्रकार" })}</Label>
        <div className="mt-2 mb-4 flex flex-wrap gap-2" data-testid="mileage-fuel-types">
          {FUEL_TYPES.map((f) => (
            <button key={f.id} type="button" onClick={() => pickFuel(f.id)} data-testid={`mileage-fuel-${f.id}`}
              className={`min-h-[40px] rounded-full border px-3.5 text-sm font-medium transition-colors ${fuel === f.id ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-600 hover:border-primary/40"}`}>
              {L(f.label)}
            </button>
          ))}
        </div>
        <div className="space-y-4">
          <Field label={L({ en: "Distance covered (km)", hi: "तय दूरी (किमी)" })} value={distance} onChange={setDistance} testId="mileage-distance" />
          <Field label={L({ en: `Fuel used (${unit})`, hi: `इस्तेमाल ईंधन (${unit})` })} value={used} onChange={setUsed} testId="mileage-used" />
          <Field label={L({ en: `Fuel price / ${unit}`, hi: `ईंधन कीमत / ${unit}` })} value={price} onChange={setPrice} testId="mileage-price" suffix="₹" />
          <Field label={L({ en: "Monthly distance (km)", hi: "मासिक दूरी (किमी)" })} value={monthlyKm} onChange={setMonthlyKm} testId="mileage-monthly" />
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <Label className="text-sm text-slate-600">{L({ en: "Advanced (trip & range)", hi: "एडवांस्ड (ट्रिप व रेंज)" })}</Label>
          <Switch checked={advanced} onCheckedChange={setAdvanced} data-testid="mileage-advanced-toggle" />
        </div>
        {advanced && (
          <div className="mt-3 space-y-4">
            <Field label={L({ en: `Fuel amount (${unit})`, hi: `ईंधन मात्रा (${unit})` })} value={fuelAmount} onChange={setFuelAmount} testId="mileage-fuelamount" />
            <Field label={L({ en: "Trip distance (km)", hi: "ट्रिप दूरी (किमी)" })} value={tripKm} onChange={setTripKm} testId="mileage-tripkm" />
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <Label className="text-sm text-slate-600">{L({ en: "Compare vehicle A vs B", hi: "वाहन A बनाम B तुलना" })}</Label>
          <Switch checked={compare} onCheckedChange={setCompare} data-testid="mileage-compare-toggle" />
        </div>
        {compare && (
          <div className="mt-3 space-y-4">
            <p className="text-xs text-slate-400">{L({ en: "Vehicle A uses the values above. Enter Vehicle B:", hi: "वाहन A ऊपर के मान इस्तेमाल करता है। वाहन B दर्ज करें:" })}</p>
            <Field label={L({ en: `Vehicle B efficiency (${effUnit})`, hi: `वाहन B दक्षता (${effUnit})` })} value={bEff} onChange={setBEff} testId="mileage-beff" />
            <Field label={L({ en: `Vehicle B fuel price / ${unit}`, hi: `वाहन B कीमत / ${unit}` })} value={bPrice} onChange={setBPrice} testId="mileage-bprice" suffix="₹" />
          </div>
        )}
      </Card>

      <Card>
        {invalid ? (
          <p className="py-8 text-center text-sm text-slate-500" data-testid="mileage-invalid">
            {L({ en: "Enter distance and fuel greater than zero to see results.", hi: "परिणाम देखने के लिए दूरी और ईंधन शून्य से अधिक दर्ज करें।" })}
          </p>
        ) : (
          <>
            <div className="divide-y divide-slate-100" data-testid="mileage-results">
              {rows.map((r, i) => <ResultRow key={i} label={L(r.label)} value={r.value} big={r.big} accent={r.accent} />)}
              {advRows.map((r, i) => <ResultRow key={`a${i}`} label={L(r.label)} value={r.value} />)}
            </div>
            {compare && (
              <div className="mt-4 rounded-xl bg-slate-50 p-4" data-testid="mileage-compare-result">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{L({ en: "A vs B", hi: "A बनाम B" })}</p>
                <ResultRow label={L({ en: "A cost/km", hi: "A लागत/किमी" })} value={fmt(round2(aCostKm))} />
                <ResultRow label={L({ en: "B cost/km", hi: "B लागत/किमी" })} value={fmt(round2(bCostKm))} />
                <ResultRow label={L({ en: "A monthly", hi: "A मासिक" })} value={fmt(Math.round(aMonthly))} />
                <ResultRow label={L({ en: "B monthly", hi: "B मासिक" })} value={fmt(Math.round(bMonthly))} />
                <div className="mt-3 rounded-lg bg-primary/10 p-3 text-center">
                  <p className="font-display text-sm font-bold text-primary" data-testid="mileage-cheaper">
                    {cheaper === "tie"
                      ? L({ en: "Both cost the same per km", hi: "दोनों की प्रति किमी लागत समान" })
                      : L({ en: `Vehicle ${cheaper} is cheaper — saves ~${fmt(Math.round(monthlySaving))}/month`, hi: `वाहन ${cheaper} सस्ता है — ~${fmt(Math.round(monthlySaving))}/माह बचत` })}
                  </p>
                </div>
              </div>
            )}
            <p className="mt-4 text-xs text-slate-400">{L({ en: "Estimates based on your inputs; real mileage varies with driving and conditions.", hi: "आपके इनपुट पर आधारित अनुमान; वास्तविक माइलेज ड्राइविंग व परिस्थितियों से बदलता है।" })}</p>
            <SaveButton onSave={onSave} isAuthed={isAuthed} testId="mileage-save-btn" />
          </>
        )}
      </Card>
    </div>
  );
}


// ---------- Custom calculators ----------
function BudgetCalc() {
  const { t, language } = useLanguage();
  const { save, isAuthed } = useSaveCalc();
  const hi = language === "hi";
  const [income, setIncome] = useState(50000);
  const [emi, setEmi] = useState(0);
  const [savingsTarget, setSavingsTarget] = useState(10000);
  const [expenses, setExpenses] = useState([
    { name: "Rent", amount: 15000, type: "fixed" },
    { name: "Groceries", amount: 8000, type: "variable" },
    { name: "Transport", amount: 3000, type: "variable" },
  ]);
  const res = calcBudget({ income, expenses, emi, savingsTarget });

  const update = (i, key, val) => setExpenses((prev) => prev.map((e, idx) => (idx === i ? { ...e, [key]: val } : e)));
  const add = () => setExpenses((prev) => [...prev, { name: "", amount: 0 }]);
  const remove = (i) => setExpenses((prev) => prev.filter((_, idx) => idx !== i));

  const onSave = () => save({
    title: `${t("calc_budget")} — ${formatCurrency(income, language)}`,
    content: `${t("monthly_income")}: ${formatCurrency(res.income, language)}\n${t("total_expenses")}: ${formatCurrency(res.totalExpenses, language)}\n${t("savings")}: ${formatCurrency(res.savings, language)} (${res.savingsRate}%)`,
    metadata: { inputs: { income, expenses }, results: res },
  });

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <Field label={t("monthly_income")} value={income} onChange={(v) => setIncome(v)} testId="budget-income" suffix="₹" />
        <div className="mt-4">
          <Label className="text-sm text-slate-600">{t("expenses")}</Label>
          <div className="mt-2 space-y-2">
            {expenses.map((e, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={e.name} onChange={(ev) => update(i, "name", ev.target.value)} placeholder={t("expense_name")} data-testid={`budget-exp-name-${i}`} className="min-h-[44px] flex-1" />
                <Input type="number" value={e.amount} onChange={(ev) => update(i, "amount", ev.target.value)} placeholder={t("amount")} data-testid={`budget-exp-amount-${i}`} className="min-h-[44px] w-24" />
                <button type="button" onClick={() => update(i, "type", e.type === "variable" ? "fixed" : "variable")} className="min-h-[44px] flex-shrink-0 rounded-lg border border-slate-200 px-2 text-[11px] font-medium text-slate-500 hover:bg-slate-50" data-testid={`budget-exp-type-${i}`}>
                  {e.type === "variable" ? (hi ? "परिवर्ती" : "Variable") : (hi ? "स्थिर" : "Fixed")}
                </button>
                <button onClick={() => remove(i)} className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-slate-300 hover:bg-rose-50 hover:text-rose-500" aria-label="remove">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={add} data-testid="budget-add-expense" className="mt-2 gap-1.5 text-primary">
            <Plus className="h-4 w-4" /> {t("add_expense")}
          </Button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Field label={hi ? "EMI / कर्ज़" : "EMI / Debt"} value={emi} onChange={setEmi} testId="budget-emi" suffix="₹" />
          <Field label={hi ? "बचत लक्ष्य" : "Savings target"} value={savingsTarget} onChange={setSavingsTarget} testId="budget-target" suffix="₹" />
        </div>
      </Card>
      <Card>
        <div className="divide-y divide-slate-100" data-testid="budget-results">
          <ResultRow label={t("monthly_income")} value={formatCurrency(res.income, language)} />
          <ResultRow label={hi ? "स्थिर खर्च" : "Fixed expenses"} value={formatCurrency(res.fixed, language)} />
          <ResultRow label={hi ? "परिवर्ती खर्च" : "Variable expenses"} value={formatCurrency(res.variable, language)} />
          {res.debt > 0 && <ResultRow label={hi ? "EMI / कर्ज़" : "EMI / Debt"} value={formatCurrency(res.debt, language)} />}
          <ResultRow label={hi ? "मासिक बचत" : "Monthly savings"} value={formatCurrency(res.savings, language)} big />
          <ResultRow label={t("savings_rate")} value={`${res.savingsRate}%`} accent />
        </div>
        <div className={`mt-3 rounded-xl p-3 text-sm ${res.vsTarget >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="budget-vs-target">
          {res.vsTarget >= 0
            ? (hi ? `आप बचत लक्ष्य से ${formatCurrency(res.vsTarget, language)} आगे हैं।` : `You're ${formatCurrency(res.vsTarget, language)} ahead of your savings target.`)
            : (hi ? `आप बचत लक्ष्य से ${formatCurrency(Math.abs(res.vsTarget), language)} पीछे हैं।` : `You're ${formatCurrency(Math.abs(res.vsTarget), language)} short of your savings target.`)}
        </div>
        {res.breakdown.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="budget-breakdown">
            <p className="text-xs font-medium text-slate-500">{hi ? "खर्च का विवरण" : "Spending breakdown"}</p>
            {res.breakdown.slice(0, 6).map((b, i) => (
              <div key={i}>
                <div className="flex justify-between text-xs text-slate-600"><span className="truncate pr-2">{b.name || (hi ? "मद" : "Item")}</span><span className="flex-shrink-0">{formatCurrency(b.amount, language)} · {b.pct}%</span></div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, b.pct)}%` }} /></div>
              </div>
            ))}
          </div>
        )}
        <SaveButton onSave={onSave} isAuthed={isAuthed} testId="budget-save-btn" />
      </Card>
    </div>
  );
}

function EmiCalc() {
  const { t, language } = useLanguage();
  const { save, isAuthed } = useSaveCalc();
  const [principal, setPrincipal] = useState(1000000);
  const [annualRate, setAnnualRate] = useState(9);
  const [years, setYears] = useState(15);
  const [prepay, setPrepay] = useState(0);
  const hi = language === "hi";
  const fmt = (n) => formatCurrency(n, language);
  const res = calcEmiPlan({ principal, annualRate, years, prepayment: prepay });

  const onSave = () => save({
    title: `${t("calc_emi")} — ${fmt(principal)}`,
    content: `${t("monthly_emi")}: ${fmt(res.emi)}\n${t("total_interest")}: ${fmt(res.totalInterest)}\n${t("total_payment")}: ${fmt(res.totalPayment)}`,
    metadata: { inputs: { principal, annualRate, years, prepay }, results: res },
  });

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <div className="space-y-4">
          <Field label={t("loan_amount")} value={principal} onChange={setPrincipal} testId="emi-principal" suffix="₹" />
          <Field label={t("interest_rate")} value={annualRate} onChange={setAnnualRate} testId="emi-rate" suffix="%" />
          <Field label={t("tenure_years")} value={years} onChange={setYears} testId="emi-years" />
          <Field label={hi ? "अतिरिक्त मासिक प्रीपेमेंट" : "Extra monthly prepayment"} value={prepay} onChange={setPrepay} testId="emi-prepay" suffix="₹" />
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setPrincipal(1000000); setAnnualRate(9); setYears(15); setPrepay(0); }} className="mt-4 text-slate-500" data-testid="emi-reset-btn">{t("reset")}</Button>
      </Card>
      <Card>
        <div className="flex items-center gap-5">
          <Donut size={128} thickness={16}
            segments={[{ value: res.principal, color: "hsl(207 90% 40%)" }, { value: res.totalInterest, color: "hsl(207 90% 78%)" }]}
            centerLabel={fmt(res.emi)} centerSub={t("monthly_emi")} />
          <div className="min-w-0 flex-1 space-y-3">
            <Legend items={[{ color: "hsl(207 90% 40%)", label: t("loan_amount") }, { color: "hsl(207 90% 78%)", label: t("total_interest") }]} />
            <div className="divide-y divide-slate-100" data-testid="emi-results">
              <ResultRow label={t("total_interest")} value={fmt(res.totalInterest)} accent />
              <ResultRow label={t("total_payment")} value={fmt(res.totalPayment)} accent />
            </div>
          </div>
        </div>
        {prepay > 0 && res.savedInterest > 0 && (
          <div className="mt-4 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="emi-prepay-impact">
            {hi
              ? `प्रीपेमेंट से आप ${fmt(res.savedInterest)} ब्याज बचाते हैं और लोन ${res.monthsSaved} महीने पहले खत्म होता है।`
              : `Prepaying saves you ${fmt(res.savedInterest)} in interest and clears the loan ${res.monthsSaved} months early.`}
          </div>
        )}
        <SaveButton onSave={onSave} isAuthed={isAuthed} testId="emi-save-btn" />
      </Card>
    </div>
  );
}

function InvestmentCalc() {
  const { t, language } = useLanguage();
  const { save, isAuthed } = useSaveCalc();
  const hi = language === "hi";
  const DEF = { lumpsum: 100000, monthly: 10000, years: 15, annualReturn: 12, stepUp: 10, inflation: 6 };
  const [s, setS] = useState(DEF);
  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v }));
  const fmt = (n) => formatCurrency(n, language);
  const res = calcInvestment(s);
  const onSave = () => save({
    title: hi ? "निवेश / SIP" : "Investment / SIP",
    content: `${hi ? "कुल निवेश" : "Total invested"}: ${fmt(res.totalInvested)}\n${hi ? "अनुमानित रिटर्न" : "Estimated returns"}: ${fmt(res.returns)}\n${hi ? "अंतिम कोष" : "Final corpus"}: ${fmt(res.corpus)}`,
    metadata: { inputs: s, results: { corpus: res.corpus, totalInvested: res.totalInvested, returns: res.returns, realValue: res.realValue } },
  });
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <div className="space-y-4">
          <Field label={hi ? "एकमुश्त निवेश" : "Lump sum (one-time)"} value={s.lumpsum} onChange={set("lumpsum")} testId="inv-lumpsum" suffix="₹" />
          <Field label={hi ? "मासिक SIP" : "Monthly SIP"} value={s.monthly} onChange={set("monthly")} testId="inv-monthly" suffix="₹" />
          <Field label={hi ? "अवधि (वर्ष)" : "Duration (years)"} value={s.years} onChange={set("years")} testId="inv-years" />
          <Field label={hi ? "अपेक्षित रिटर्न" : "Expected return"} value={s.annualReturn} onChange={set("annualReturn")} testId="inv-return" suffix="%" />
          <Field label={hi ? "वार्षिक स्टेप-अप" : "Annual step-up"} value={s.stepUp} onChange={set("stepUp")} testId="inv-stepup" suffix="%" />
          <Field label={hi ? "महंगाई" : "Inflation"} value={s.inflation} onChange={set("inflation")} testId="inv-inflation" suffix="%" />
        </div>
        <Button variant="ghost" size="sm" onClick={() => setS(DEF)} className="mt-4 text-slate-500" data-testid="inv-reset-btn">{t("reset")}</Button>
      </Card>
      <Card>
        <div className="rounded-xl bg-slate-950 p-4 text-white" data-testid="inv-results">
          <p className="text-xs text-slate-300">{hi ? "अनुमानित अंतिम कोष" : "Estimated final corpus"}</p>
          <p className="font-display text-3xl font-extrabold">{fmt(res.corpus)}</p>
          <p className="mt-1 text-xs text-slate-400">{hi ? "आज के मूल्य में लगभग" : "≈ worth today"} {fmt(res.realValue)}</p>
        </div>
        <div className="mt-4 divide-y divide-slate-100">
          <ResultRow label={hi ? "कुल निवेश" : "Total invested"} value={fmt(res.totalInvested)} />
          <ResultRow label={hi ? "अनुमानित रिटर्न" : "Estimated returns"} value={fmt(res.returns)} accent />
        </div>
        {res.series.length > 0 && (
          <div className="mt-4">
            <p className="mb-1.5 text-xs font-medium text-slate-500">{hi ? "साल-दर-साल वृद्धि" : "Year-by-year growth"}</p>
            <GrowthBars data={res.series} valueKey="value" baseKey="invested" />
            <div className="mt-2"><Legend items={[{ color: "hsl(207 90% 40%)", label: hi ? "निवेश" : "Invested" }, { color: "hsl(207 90% 78%)", label: hi ? "रिटर्न" : "Returns" }]} /></div>
          </div>
        )}
        <p className="mt-3 text-[11px] leading-relaxed text-slate-400">{hi ? "अनुमान केवल उदाहरण के लिए; रिटर्न की गारंटी नहीं है और बाजार के अनुसार बदलते हैं।" : "Estimates for illustration only — returns are not guaranteed and vary with the market."}</p>
        <SaveButton onSave={onSave} isAuthed={isAuthed} testId="inv-save-btn" />
      </Card>
    </div>
  );
}

function SavingsGoalCalc() {
  const { t, language } = useLanguage();
  const { save, isAuthed } = useSaveCalc();
  const hi = language === "hi";
  const DEF = { goal: 500000, current: 50000, monthly: 10000, annualReturn: 7, months: 36, inflation: 6 };
  const [s, setS] = useState(DEF);
  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v }));
  const fmt = (n) => formatCurrency(n, language);
  const res = calcSavingsGoalPlan(s);
  const progress = Math.min(100, Math.round((res.projected / (Number(s.goal) || 1)) * 100));
  const onSave = () => save({
    title: hi ? "स्मार्ट बचत लक्ष्य" : "Smart Savings Goal",
    content: `${hi ? "लक्ष्य" : "Goal"}: ${fmt(s.goal)}\n${hi ? "अनुमानित" : "Projected"}: ${fmt(res.projected)}\n${hi ? "आवश्यक मासिक बचत" : "Required monthly"}: ${fmt(res.requiredMonthly)}`,
    metadata: { inputs: s, results: { projected: res.projected, requiredMonthly: res.requiredMonthly, onTrack: res.onTrack } },
  });
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <div className="space-y-4">
          <Field label={hi ? "लक्ष्य राशि" : "Target amount"} value={s.goal} onChange={set("goal")} testId="sg-goal" suffix="₹" />
          <Field label={hi ? "वर्तमान बचत" : "Current savings"} value={s.current} onChange={set("current")} testId="sg-current" suffix="₹" />
          <Field label={hi ? "मासिक योगदान" : "Monthly contribution"} value={s.monthly} onChange={set("monthly")} testId="sg-monthly" suffix="₹" />
          <Field label={hi ? "समय (महीने)" : "Target in (months)"} value={s.months} onChange={set("months")} testId="sg-months" />
          <Field label={hi ? "अपेक्षित रिटर्न" : "Expected return"} value={s.annualReturn} onChange={set("annualReturn")} testId="sg-return" suffix="%" />
          <Field label={hi ? "महंगाई" : "Inflation"} value={s.inflation} onChange={set("inflation")} testId="sg-inflation" suffix="%" />
        </div>
        <Button variant="ghost" size="sm" onClick={() => setS(DEF)} className="mt-4 text-slate-500" data-testid="sg-reset-btn">{t("reset")}</Button>
      </Card>
      <Card>
        <div className={`rounded-xl p-4 ${res.onTrack ? "bg-emerald-50" : "bg-amber-50"}`} data-testid="sg-status">
          <p className={`font-display text-base font-bold ${res.onTrack ? "text-emerald-700" : "text-amber-700"}`}>
            {res.onTrack ? (hi ? "आप लक्ष्य पर हैं" : "You're on track") : (hi ? "आप लक्ष्य से पीछे हैं" : "You're behind your goal")}
          </p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white">
            <div className={`h-full rounded-full ${res.onTrack ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-1 text-xs text-slate-500">{progress}% {hi ? "लक्ष्य का अनुमानित" : "of goal projected"}</p>
        </div>
        <div className="mt-4 divide-y divide-slate-100" data-testid="sg-results">
          <ResultRow label={hi ? "लक्ष्य तिथि पर अनुमानित" : "Projected at target date"} value={fmt(res.projected)} big />
          <ResultRow label={res.shortfall > 0 ? (hi ? "कमी" : "Shortfall") : (hi ? "अधिशेष" : "Surplus")} value={fmt(Math.abs(res.shortfall))} accent />
          <ResultRow label={hi ? "आवश्यक मासिक बचत" : "Required monthly saving"} value={fmt(res.requiredMonthly)} />
          <ResultRow label={hi ? "लक्ष्य का आज का मूल्य" : "Goal in today's money"} value={fmt(res.realValueOfGoal)} />
        </div>
        {res.extraMonthly > 0 && (
          <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600" data-testid="sg-extra">
            {hi ? `समय पर लक्ष्य पाने के लिए ${fmt(res.extraMonthly)} प्रति माह अतिरिक्त बचाएँ।` : `Save ${fmt(res.extraMonthly)} more per month to reach your goal on time.`}
          </div>
        )}
        {res.series.length > 0 && (
          <div className="mt-4">
            <p className="mb-1.5 text-xs font-medium text-slate-500">{hi ? "समय के साथ प्रगति" : "Progress over time"}</p>
            <GrowthBars data={res.series} valueKey="value" baseKey="invested" />
          </div>
        )}
        <SaveButton onSave={onSave} isAuthed={isAuthed} testId="sg-save-btn" />
      </Card>
    </div>
  );
}

function RentBuyCalc() {
  const { t, language } = useLanguage();
  const { save, isAuthed } = useSaveCalc();
  const hi = language === "hi";
  const [s, setS] = useState({
    monthlyRent: 20000, rentIncrease: 5, homePrice: 6000000, downPayment: 1200000,
    loanRate: 9, loanYears: 20, maintenance: 3000, years: 10, appreciation: 6, opportunityReturn: 8,
  });
  const set = (k) => (v) => setS((prev) => ({ ...prev, [k]: v }));
  const res = calcRentVsBuy(s);
  const verdict = res.cheaper === "rent" ? t("rentbuy_rent_better") : res.cheaper === "buy" ? t("rentbuy_buy_better") : t("rentbuy_equal");

  const onSave = () => save({
    title: `${t("calc_rentbuy")} — ${s.years} yrs`,
    content: `${t("total_rent_cost")}: ${formatCurrency(res.totalRent, language)}\n${t("total_buy_cost")}: ${formatCurrency(res.totalBuy, language)}\n${verdict}`,
    metadata: { inputs: s, results: res },
  });

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <div className="space-y-3">
          <Field label={t("monthly_rent")} value={s.monthlyRent} onChange={set("monthlyRent")} testId="rb-rent" suffix="₹" />
          <Field label={t("rent_increase")} value={s.rentIncrease} onChange={set("rentIncrease")} testId="rb-rentinc" suffix="%" />
          <Field label={t("home_price")} value={s.homePrice} onChange={set("homePrice")} testId="rb-price" suffix="₹" />
          <Field label={t("down_payment")} value={s.downPayment} onChange={set("downPayment")} testId="rb-down" suffix="₹" />
          <Field label={t("loan_rate")} value={s.loanRate} onChange={set("loanRate")} testId="rb-rate" suffix="%" />
          <Field label={t("loan_tenure")} value={s.loanYears} onChange={set("loanYears")} testId="rb-loanyears" />
          <Field label={t("maintenance")} value={s.maintenance} onChange={set("maintenance")} testId="rb-maint" suffix="₹" />
          <Field label={t("horizon_years")} value={s.years} onChange={set("years")} testId="rb-years" />
          <Field label={hi ? "संपत्ति वृद्धि दर" : "Property appreciation"} value={s.appreciation} onChange={set("appreciation")} testId="rb-appr" suffix="%" />
          <Field label={hi ? "निवेश रिटर्न (अवसर लागत)" : "Investment return (opportunity)"} value={s.opportunityReturn} onChange={set("opportunityReturn")} testId="rb-opp" suffix="%" />
        </div>
      </Card>
      <Card>
        <div className="divide-y divide-slate-100" data-testid="rb-results">
          <ResultRow label={hi ? "किराया — शुद्ध लागत" : "Renting — net cost"} value={formatCurrency(res.netRent, language)} accent />
          <ResultRow label={hi ? "खरीद — शुद्ध लागत" : "Buying — net cost"} value={formatCurrency(res.netBuy, language)} accent />
          <ResultRow label={hi ? "अवधि पर घर का मूल्य" : "Home value at horizon"} value={formatCurrency(res.homeFuture, language)} />
          <ResultRow label={hi ? "आपकी इक्विटी" : "Your equity"} value={formatCurrency(res.equity, language)} />
        </div>
        <div className="mt-4 rounded-xl bg-primary/10 p-4 text-center" data-testid="rb-verdict">
          <p className="font-display text-base font-bold text-primary">{verdict}</p>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {hi ? "इसमें किराया वृद्धि, लोन चुकौती, संपत्ति मूल्यवृद्धि और डाउन पेमेंट की अवसर लागत शामिल है। ये केवल अनुमान हैं — कर, ब्रोकरेज और रखरखाव भिन्न हो सकते हैं।" : "Includes rent growth, loan payoff, property appreciation and the opportunity cost of your down payment. Estimates only — taxes, brokerage and upkeep vary."}
        </div>
        <SaveButton onSave={onSave} isAuthed={isAuthed} testId="rb-save-btn" />
      </Card>
    </div>
  );
}
